<template>
  <div class="create-event-page container-fluid p-0">
    <FullScreenLoader v-if="loading" />
    <div class="steps-navigation" v-if="items.length > 0 && !loading">
      <div class="progress-track">
        <div
          class="progress-fill"
          :style="{ width: ((currentStep - 1) / (steps.length - 1) * 100) + '%' }"
        ></div>
      </div>

      <div class="steps-row">
        <div
          v-for="step in steps"
          :key="step.id"
          :class="['step-dot-item', {
            active: currentStep === step.id,
            completed: currentStep > step.id,
          }]"
          @click="goToStep(step.id)"
        >
          <div class="step-dot">
            <i v-if="currentStep > step.id" class="fas fa-check"></i>
            <span v-else>{{ step.id }}</span>
          </div>
          <span class="step-dot-label">{{ step.name }}</span>
        </div>
      </div>
    </div>

    <main v-show="!loading" class="step-content-wrapper">
      <div class="container-fluid">
        <div v-if="event && items.length > 0">
          <CartHeader :event="event" />

          <div v-show="currentStep === 1">
            <div class="summary-card card border-0 rounded-4 shadow-sm mb-3">
              <div class="summary-card-header d-flex justify-content-between align-items-center">
                <span class="summary-card-label">Seus presentes</span>
                <router-link :to="`/cart/${eventId}`" class="btn-edit-items">
                  <i class="fa-solid fa-arrow-left me-1"></i>Editar Itens
                </router-link>
              </div>
              <div class="summary-card-items">
                <div v-for="item in items" :key="item.id" class="summary-item-line">
                  <span class="summary-item-name">{{ item.quantity }}x {{ item.name }}</span>
                  <span class="summary-item-price">R$ {{ formatCurrency(item.quantity * item.price) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-show="currentStep === 2" class="payment-step mt-3">
            <div class="payment-step-header">
              <span class="payment-step-label">Forma de pagamento</span>
              <span class="payment-step-total">Total: <strong>R$ {{ totalFormatted }}</strong></span>
            </div>
            <div class="pay-grid">
              <button
                class="pay-card"
                :class="{ 'pay-card--selected': metodoSelecionado === 'pix' }"
                @click="confirmarMetodo('pix')"
              >
                <span class="pay-card__check"><i class="fa-solid fa-check"></i></span>
                <span class="pay-card__icon pay-card__icon--pix">
                  <i class="fa-solid fa-qrcode"></i>
                </span>
                <div class="pay-card__text">
                  <strong class="pay-card__title">Pix</strong>
                  <span class="pay-card__desc">Aprovação instantânea</span>
                </div>
                <span class="pay-card__tag pay-card__tag--green">
                  <i class="fa-solid fa-bolt"></i> Mais rápido
                </span>
              </button>
              <button
                class="pay-card"
                :class="{ 'pay-card--selected': metodoSelecionado === 'card' }"
                @click="confirmarMetodo('card')"
              >
                <span class="pay-card__check"><i class="fa-solid fa-check"></i></span>
                <span class="pay-card__icon pay-card__icon--blue">
                  <i class="fa-solid fa-credit-card"></i>
                </span>
                <div class="pay-card__text">
                  <strong class="pay-card__title">Cartão</strong>
                  <span class="pay-card__desc">Parcele em até 12x</span>
                </div>
                <span class="pay-card__tag pay-card__tag--blue">
                  <i class="fa-solid fa-layer-group"></i> Parcelado
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>

    <footer v-if="items.length > 0 && !loading" class="app-footer">
      <div class="app-footer__inner">
        <p class="app-footer__step-hint">
          Passo {{ currentStep }} de {{ steps.length }} —
          <strong>{{ steps[currentStep - 1].name }}</strong>
        </p>

        <div class="app-footer__actions">
          <button
            class="btn btn-footer-back"
            @click="backPage"
          >
            <i class="fas fa-chevron-left me-1"></i> Voltar
          </button>

          <button
            v-if="currentStep < steps.length"
            class="btn btn-footer-next"
            @click="nextStep"
          >
            Próximo <i class="fas fa-arrow-right ms-1"></i>
          </button>

          <button
            v-else
            class="btn btn-footer-finish"
            @click="requestContinue"
          >
            <i class="fa-solid fa-lock me-1"></i> Continuar
          </button>
        </div>
      </div>
    </footer>

    <BottomModal v-if="metodoSelecionado" @close="metodoSelecionado = null">
      <template #default>
        <div class="confirm-modal-icon mb-3">
          <div :class="['confirm-icon-box', metodoSelecionado === 'pix' ? 'pix' : 'card']">
            <i :class="metodoSelecionado === 'pix' ? 'fa-solid fa-qrcode' : 'fa-solid fa-credit-card'"></i>
          </div>
        </div>
        <h5 class="text-center fw-bold mb-1">
          Pagar com {{ metodoSelecionado === 'pix' ? 'Pix' : 'Cartão de crédito' }}
        </h5>
        <p class="text-center text-muted small mb-1">
          Você será direcionado para uma
          <span class="fw-semibold text-success"><i class="fa-solid fa-lock me-1"></i>tela segura</span>
          de pagamento.
        </p>
        <p class="text-center text-muted small mb-4">
          Total: <strong class="text-dark">R$ {{ totalFormatted }}</strong>
        </p>
        <div class="d-flex flex-row gap-2">
          <button class="btn btn-outline-secondary w-100" @click="metodoSelecionado = null">
            <i class="fas fa-chevron-down me-2"></i> Trocar forma
          </button>
          <button class="btn btn-color-site w-100" @click="irParaPagamento">
            <i class="fa-solid fa-lock me-2"></i> Continuar
          </button>
        </div>
      </template>
    </BottomModal>
  </div>
</template>

<script setup>
import CartHeader from '@/components/CartHeader.vue';
import BottomModal from '@/components/BottomModal.vue';
import FullScreenLoader from '@/components/FullScreenLoader.vue';
import { toast } from 'vue3-toastify';
import { useCartStore } from '@/stores/cartStore';
import { useRouter, useRoute } from 'vue-router';
import { useEventTheme } from '@/composables/useEventTheme';
import { ref, computed, watch, onMounted } from 'vue';
import { formatCurrency } from '@/utils/currencyFormatter';

const router = useRouter();
const route = useRoute();
const eventId = route.params.id;
const { isReady: themeReady } = useEventTheme(eventId, { cleanup: true });
const loading = ref(true);
watch(themeReady, (ready) => {
  if (ready) setTimeout(() => { loading.value = false }, 200);
}, { immediate: true });
const cart = useCartStore();

const steps = [
  { id: 1, name: 'Resumo' },
  { id: 2, name: 'Pagamento' },
];

const currentStep = ref(1);
const metodoSelecionado = ref(null);

const items = computed(() => cart.getEvent(eventId)?.items || []);
const event = computed(() => cart.getEvent(eventId));
const totalNumber = computed(() => items.value.reduce((sum, item) => sum + item.price * item.quantity, 0));
const totalFormatted = computed(() => formatCurrency(totalNumber.value));

onMounted(() => {
  if (items.value.length <= 0) {
    router.replace({ name: 'EventDetails', params: { id: eventId } });
  }
});

function goToStep(stepId) {
  if (stepId === currentStep.value + 1) {
    nextStep();
  } else if (stepId < currentStep.value) {
    currentStep.value = stepId;
  } else if (stepId !== currentStep.value) {
    toast.info('Por favor, avance passo a passo.');
  }
}

function nextStep() {
  if (currentStep.value < steps.length) {
    currentStep.value += 1;
  }
}

function backPage() {
  if (currentStep.value > 1) {
    currentStep.value -= 1;
    return;
  }
  router.back();
}

function confirmarMetodo(metodo) {
  metodoSelecionado.value = metodo;
}

function requestContinue() {
  if (!metodoSelecionado.value) {
    toast.error('Selecione uma forma de pagamento para continuar.');
    return;
  }
  irParaPagamento();
}

function irParaPagamento() {
  router.push({ name: 'PaymentView', params: { id: eventId }, query: { ref: metodoSelecionado.value } });
}
</script>

<style scoped>
.create-event-page {
  display: flex;
  flex-direction: column;
  background-color: var(--event-bg, #f8f9fa);
  min-height: 100%;
  width: 100%;
  max-width: 100%;
  overflow-x: clip;
  box-sizing: border-box;
}

.steps-navigation {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 90;
  background-color: #fff;
  border-bottom: 1px solid #f0f0f0;
  padding: 1rem 1.5rem 0.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  margin-top: 0;
}

.progress-track {
  position: relative;
  height: 3px;
  background-color: #e9ecef;
  border-radius: 99px;
  margin: 0 auto 1rem;
  max-width: 320px;
}

.progress-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, var(--event-primary, #ff50a3), color-mix(in srgb, var(--event-primary, #ff50a3) 65%, #fff));
  border-radius: 99px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.steps-row {
  display: flex;
  justify-content: space-around;
  max-width: 360px;
  margin: 0 auto;
  gap: 8px;
}

.step-dot-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  gap: 5px;
  opacity: 0.45;
  transition: opacity 0.2s;
  min-width: 0;
  flex: 1;
}

.step-dot-item.active,
.step-dot-item.completed {
  opacity: 1;
}

.step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #9e9e9e;
  transition: all 0.3s ease;
}

.step-dot-item.active .step-dot {
  background-color: var(--event-primary, #ff50a3);
  color: #fff;
  box-shadow: 0 0 0 4px var(--event-primary-shadow, rgba(255, 80, 163, 0.18));
}

.step-dot-item.completed .step-dot {
  background-color: #32355d;
  color: #fff;
}

.step-dot-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #9e9e9e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.step-dot-item.active .step-dot-label {
  color: var(--event-primary, #ff50a3);
}

.step-dot-item.completed .step-dot-label {
  color: #32355d;
}

.step-content-wrapper {
  flex-grow: 1;
  padding: 1.5rem 1rem 2rem;
  max-width: 750px;
  margin: 0 auto;
  width: 100%;
  min-width: 0;
  overflow-x: clip;
  box-sizing: border-box;
}

.step-content-wrapper > .container-fluid {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  overflow-x: clip;
  box-sizing: border-box;
}

@media (max-width: 767.98px) {
  .step-content-wrapper > .container-fluid {
    margin-top: 1rem;;
    min-width: inherit;
  }

}

.summary-card {
  border: 1px solid #f0f0f0 !important;
  overflow: hidden;
  background: #fff;
}

.summary-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.summary-card-label {
  font-size: 0.88rem;
  font-weight: 700;
  color: #32355d;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.btn-edit-items {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--event-primary, #ff50a3);
  background: var(--event-primary-light, #fff0f6);
  border: 1px solid color-mix(in srgb, var(--event-primary, #ff50a3) 25%, transparent);
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  text-decoration: none;
  transition: background 0.15s;
}

.btn-edit-items:hover {
  background: color-mix(in srgb, var(--event-primary, #ff50a3) 12%, #fff);
}

.summary-card-items {
  padding: 0.5rem 1rem;
}

.summary-item-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.55rem 0;
  border-bottom: 1px solid #f5f5f5;
  font-size: 0.92rem;
}

.summary-item-line:last-child {
  border-bottom: none;
}

.summary-item-name {
  color: #495057;
  flex: 1;
  margin-right: 1rem;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.summary-item-price {
  font-weight: 700;
  color: #32355d;
  white-space: nowrap;
}

/* ── Step 2: Forma de pagamento ─────── */
.payment-step {
  margin-bottom: 2rem;
}

.payment-step-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 0 0.1rem;
}

.payment-step-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #a0a5b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.payment-step-total {
  font-size: 0.96rem;
  font-weight: 800;
  color: #32355d;
}

.pay-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
}

.pay-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.35rem;
  padding: 1.65rem 1rem 1.25rem;
  background: #fff;
  border: 2px solid #eef0f5;
  border-radius: 1.15rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s, transform 0.15s;
  width: 100%;
  overflow: hidden;
}

.pay-card:hover {
  border-color: color-mix(in srgb, var(--event-primary, #ff50a3) 50%, transparent);
  background: var(--event-primary-light, #fff0f6);
  box-shadow: 0 8px 24px color-mix(in srgb, var(--event-primary, #ff50a3) 12%, rgba(0, 0, 0, 0.06));
  transform: translateY(-3px);
}

.pay-card--selected {
  border-color: var(--event-primary, #ff50a3);
  background: var(--event-primary-light, #fff0f6);
  box-shadow: 0 6px 20px var(--event-primary-shadow, rgba(255, 80, 163, 0.18));
}

.pay-card__check {
  position: absolute;
  top: 0.65rem;
  right: 0.7rem;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--event-primary, #ff50a3);
  color: #fff;
  font-size: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: scale(0.4);
  transition: opacity 0.2s, transform 0.2s;
}

.pay-card--selected .pay-card__check {
  opacity: 1;
  transform: scale(1);
}

.pay-card__icon {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  margin-bottom: 0.3rem;
  flex-shrink: 0;
}

.pay-card__icon--pix {
  background: #e8f5e9;
  color: #2e7d32;
}

.pay-card__icon--blue {
  background: #e3f2fd;
  color: #1565c0;
}

.pay-card__text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
}

.pay-card__title {
  font-size: 1rem;
  font-weight: 800;
  color: #32355d;
  line-height: 1.2;
}

.pay-card__desc {
  font-size: 0.78rem;
  color: #a0a5b8;
}

.pay-card__tag {
  margin-top: 0.5rem;
  font-size: 0.69rem;
  font-weight: 700;
  padding: 0.22rem 0.65rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.pay-card__tag--green {
  background: #e8f5e9;
  color: #2e7d32;
}

.pay-card__tag--blue {
  background: #e3f2fd;
  color: #1565c0;
}

.app-footer {
  position: sticky;
  bottom: 0;
  width: 100%;
  z-index: 100;
  background: transparent;
  pointer-events: none;
}

.app-footer__inner {
  background: #fff;
  border-top: 1px solid #f0f0f0;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.07);
  padding: 0.75rem 1.25rem 1rem;
  pointer-events: all;
  max-width: 640px;
  margin: 0 auto;
  border-radius: 16px 16px 0 0;
}

.app-footer__step-hint {
  font-size: 0.72rem;
  color: #b0b0b0;
  text-align: center;
  margin-bottom: 0.6rem;
}

.app-footer__actions {
  display: flex;
  gap: 10px;
}

.btn-footer-back {
  background: #fff;
  border: 1.5px solid #e0e0e0;
  color: #6c757d;
  border-radius: 0.75rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.88rem;
  font-weight: 600;
  transition: all 0.18s;
  flex-shrink: 0;
}

.btn-footer-back:hover:not(:disabled) {
  border-color: #adb5bd;
  color: #32355d;
}

.btn-footer-back:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.btn-footer-next {
  flex: 1;
  background: var(--event-primary, #ff50a3);
  color: #fff !important;
  border: none;
  border-radius: 0.75rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.92rem;
  font-weight: 700;
  transition: background 0.18s, transform 0.15s, box-shadow 0.18s;
}

.btn-footer-next:hover {
  background: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000);
  opacity: 1;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px var(--event-primary-shadow, rgba(255, 80, 163, 0.35));
}

.btn-footer-next:focus,
.btn-footer-next:active,
.btn-footer-next:focus-visible {
  background: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000);
  color: #fff !important;
  outline: none;
  box-shadow: 0 0 0 0.2rem var(--event-primary-shadow, rgba(255, 80, 163, 0.35));
}

.btn-footer-finish {
  flex: 1;
  background: var(--event-primary, #ff50a3) !important;
  background-image: none !important;
  color: #fff !important;
  border: none;
  border-radius: 0.75rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.92rem;
  font-weight: 700;
  box-shadow: 0 4px 14px var(--event-primary-shadow, rgba(255, 80, 163, 0.35));
  transition: opacity 0.18s, transform 0.15s, box-shadow 0.18s;
}

.btn-footer-finish:hover {
  background: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000) !important;
  background-image: none !important;
  color: #fff !important;
  opacity: 1;
  transform: translateY(-1px);
}

.btn-footer-finish:focus,
.btn-footer-finish:active,
.btn-footer-finish:focus-visible {
  background: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000) !important;
  background-image: none !important;
  color: #fff !important;
  outline: none;
  box-shadow: 0 0 0 0.2rem var(--event-primary-shadow, rgba(255, 80, 163, 0.35));
}

.confirm-modal-icon {
  display: flex;
  justify-content: center;
}

.confirm-icon-box {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
}

.confirm-icon-box.pix {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.confirm-icon-box.card {
  background-color: #e3f2fd;
  color: #1565c0;
}

.btn-color-site {
  background-color: var(--event-primary, #ff50a3);
  color: #fff;
  border: 1px solid var(--event-primary, #ff50a3);
  font-weight: 600;
  transition: background-color 0.2s ease;
}

.btn-color-site:hover {
  background-color: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000);
  border-color: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000);
  color: #fff;
}

@media (max-width: 767.98px) {
  .create-event-page {
    min-width: 0;
  }

  .steps-navigation {
    position: -webkit-sticky;
    position: sticky;
    top: -1rem;
    z-index: 200;
    margin-top: -1rem;
    padding: 0.75rem 1rem 0.65rem;
  }

  .step-dot {
    width: 26px;
    height: 26px;
  }

  .step-dot-label {
    font-size: 0.68rem;
  }

  .app-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1031;
  }

  .app-footer__inner {
    max-width: 100%;
    border-radius: 12px 12px 0 0;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .btn-footer-back,
  .btn-footer-next,
  .btn-footer-finish {
    padding: 0.6rem 0.9rem;
    font-size: 0.86rem;
  }

  .step-content-wrapper {    
    padding-bottom: 100px;
  }

  .pay-card {
    padding: 1.25rem 0.75rem 1rem;
  }

  .pay-card__icon {
    width: 46px;
    height: 46px;
    font-size: 1.15rem;
  }

  .pay-card__title {
    font-size: 0.88rem;
  }

  .pay-card__desc {
    font-size: 0.72rem;
  }

  .summary-item-line {
    align-items: flex-start;
    gap: 0.5rem;
  }

  .summary-item-name {
    margin-right: 0;
    font-size: 0.9rem;
    line-height: 1.25;
  }

}

</style>